import { FilePlus2, Folder, FolderInput, FolderPlus } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";

import { BasicTooltip } from "../../code-editor/BasicTooltip";
import { Button } from "../../Button";
import { cn } from "../../utils";
import { FileIcon } from "../FileIcon";

import { FileTreeInlineEditor } from "./InlineEditor";
import { FileTreeItem } from "./Item";
import { useFolderImport } from "./hooks/useFolderImport";
import { useRootDragAndDrop } from "./hooks/useRootDragAndDrop";
import type { FileTreeNode, PendingFileTreeAction } from "./types";
import { buildTreeFromPaths, collectExpandedFoldersForPath } from "./utils";

export type { FileTreeNode, PendingFileTreeAction } from "./types";
export { getFirstFilePath } from "./utils";

export interface FileTreeProps {
  /** Flat paths (viewer); trailing `/` denotes empty folder. Ignored when `tree` is provided. */
  filePaths?: string[];
  /** Pre-built tree (editor). Takes precedence over `filePaths`. */
  tree?: FileTreeNode[];
  modifiedFilePaths?: ReadonlySet<string>;
  selectedPath: string | null;
  activeDirectoryPath?: string | null;
  onSelectFile: (path: string | null) => void;
  onSelectDirectory?: (path: string | null) => void;
  /** When both are set with `onToggleFolder`, expansion is controlled (editor). */
  expandedFolders?: Set<string>;
  onToggleFolder?: (path: string) => void;
  onMoveNode?: (
    sourcePath: string,
    sourceType: "file" | "folder",
    targetFolderPath: string | null,
  ) => void;
  onDeleteNode?: (path: string, type: "file" | "folder") => void;
  onCreateFile?: (path: string | null) => void;
  onCreateFolder?: (path: string | null) => void;
  onRequestRename?: (path: string, type: "file" | "folder") => void;
  pendingAction?: PendingFileTreeAction | null;
  onPendingActionValueChange?: (value: string) => void;
  onSubmitPendingAction?: () => void;
  onCancelPendingAction?: () => void;
  readOnly?: boolean;
  emptyMessage?: string;
  emptyStateContent?: ReactNode;
  onFolderImport?: (
    files?: FileList,
    items?: DataTransferItemList,
  ) => Promise<boolean | void> | boolean | void;
  folderImportDisabled?: boolean;
  isNodeProtected?: (path: string) => boolean;
  renderFileIcon?: (path: string, className?: string) => ReactNode;
  headerLabel?: string;
  className?: string;
}

export const FileTree = ({
  filePaths,
  tree,
  modifiedFilePaths,
  selectedPath,
  activeDirectoryPath = null,
  onSelectFile,
  onSelectDirectory,
  expandedFolders: expandedFoldersProp,
  onToggleFolder: onToggleFolderProp,
  onMoveNode,
  onDeleteNode,
  onCreateFile,
  onCreateFolder,
  onRequestRename,
  pendingAction,
  onPendingActionValueChange,
  onSubmitPendingAction,
  onCancelPendingAction,
  readOnly = false,
  emptyMessage = "No files yet",
  emptyStateContent,
  onFolderImport,
  folderImportDisabled = false,
  isNodeProtected,
  renderFileIcon,
  headerLabel = "Files",
  className,
}: FileTreeProps) => {
  const displayTree = useMemo(() => {
    if (tree !== undefined) {
      return tree;
    }
    if (filePaths !== undefined) {
      return buildTreeFromPaths(filePaths);
    }
    return [];
  }, [filePaths, tree]);

  const isControlledExpansion =
    expandedFoldersProp !== undefined && onToggleFolderProp !== undefined;

  const [internalExpandedFolders, setInternalExpandedFolders] = useState<Set<string>>(new Set());

  const effectiveExpandedFolders = isControlledExpansion
    ? expandedFoldersProp
    : internalExpandedFolders;

  const toggleFolder = (path: string) => {
    if (isControlledExpansion) {
      onToggleFolderProp?.(path);
      return;
    }
    setInternalExpandedFolders(previous => {
      const next = new Set(previous);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  useEffect(() => {
    if (isControlledExpansion || !selectedPath) {
      return;
    }
    setInternalExpandedFolders(previous => {
      const next = new Set(previous);
      collectExpandedFoldersForPath(selectedPath).forEach(folder => next.add(folder));
      return next;
    });
  }, [isControlledExpansion, selectedPath]);

  const {
    draggedItem,
    isRootDragOver,
    setDraggedItem,
    clearDragState,
    setIsRootDragOver,
    handleRootDragOver,
    handleRootDragLeave,
    handleRootDrop,
  } = useRootDragAndDrop({
    readOnly,
    onMoveNode,
  });

  const {
    folderImportInputRef,
    handleTreeDragLeave,
    handleOsFolderImportDragOverCapture,
    handleOsFolderImportDropCapture,
    handleFolderImportButtonClick,
    handleFolderInputChange,
  } = useFolderImport({
    readOnly,
    onFolderImport,
    folderImportDisabled,
    onExternalDragOver: () => setIsRootDragOver(true),
    onExternalDragLeave: () => setIsRootDragOver(false),
    onFolderImportDrop: clearDragState,
  });

  const handleCreateFileAtRoot = onCreateFile ? () => onCreateFile(null) : undefined;
  const handleCreateFolderAtRoot = onCreateFolder ? () => onCreateFolder(null) : undefined;

  const isCreatingAtRoot = pendingAction?.mode === "create" && pendingAction.parentPath === null;
  const isTreeEmpty = displayTree.length === 0;

  return (
    <div
      className={cn("flex flex-1 flex-col overflow-hidden", className)}
      onDragLeave={handleTreeDragLeave}
      onDragOverCapture={handleOsFolderImportDragOverCapture}
      onDropCapture={handleOsFolderImportDropCapture}
    >
      {!readOnly && (
        <div className="flex h-[37px] items-center justify-between border-b border-gray-200 bg-gray-50/70 px-3">
          <span className="text-sm font-medium text-gray-700">{headerLabel}</span>
          <div className="flex items-center gap-1">
            {onFolderImport && (
              <>
                <input
                  ref={folderImportInputRef}
                  type="file"
                  className="sr-only"
                  multiple
                  // @ts-expect-error Chromium / Safari directory picker
                  webkitdirectory=""
                  onChange={handleFolderInputChange}
                />
                <BasicTooltip content="Import files">
                  <span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 rounded-md p-0 text-gray-600 hover:text-gray-900"
                      disabled={folderImportDisabled}
                      onClick={() => {
                        void handleFolderImportButtonClick();
                      }}
                    >
                      <FolderInput className="h-4 w-4" />
                    </Button>
                  </span>
                </BasicTooltip>
              </>
            )}
            {onCreateFolder && (
              <BasicTooltip content="New folder">
                <span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 rounded-md p-0 text-gray-600 hover:text-gray-900"
                    onClick={handleCreateFolderAtRoot}
                  >
                    <FolderPlus className="h-4 w-4" />
                  </Button>
                </span>
              </BasicTooltip>
            )}
            {onCreateFile && (
              <BasicTooltip content="New file">
                <span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 rounded-md p-0 text-gray-600 hover:text-gray-900"
                    onClick={handleCreateFileAtRoot}
                  >
                    <FilePlus2 className="h-4 w-4" />
                  </Button>
                </span>
              </BasicTooltip>
            )}
          </div>
        </div>
      )}
      {readOnly && (
        <div className="flex h-[37px] items-center border-b border-gray-200 bg-gray-50/70 px-3">
          <span className="text-sm font-medium text-gray-700">{headerLabel}</span>
        </div>
      )}
      <div
        className={cn(
          "flex-1 overflow-y-auto p-2.5 transition-all duration-200",
          isRootDragOver && "bg-blue-50/60 ring-2 ring-inset ring-blue-400",
        )}
        onDragOver={readOnly || !onMoveNode ? undefined : handleRootDragOver}
        onDragLeave={readOnly || !onMoveNode ? undefined : handleRootDragLeave}
        onDrop={readOnly || !onMoveNode ? undefined : handleRootDrop}
      >
        <div className="space-y-1">
          {pendingAction?.mode === "create" &&
            pendingAction.parentPath === null &&
            onPendingActionValueChange &&
            onSubmitPendingAction &&
            onCancelPendingAction && (
              <FileTreeInlineEditor
                value={pendingAction.value}
                error={pendingAction.error}
                placeholder={pendingAction.itemType === "folder" ? "folder-name" : "file-name.md"}
                paddingLeft={pendingAction.itemType === "folder" ? 12 : 8}
                leadingContent={
                  pendingAction.itemType === "folder" ? (
                    <Folder className="h-4 w-4 shrink-0 text-gray-500" />
                  ) : (
                    (renderFileIcon?.(pendingAction.value || "new-file.md") ?? (
                      <FileIcon path={pendingAction.value || "new-file.md"} />
                    ))
                  )
                }
                onChange={onPendingActionValueChange}
                onSubmit={onSubmitPendingAction}
                onCancel={onCancelPendingAction}
              />
            )}
          {isTreeEmpty &&
            !isCreatingAtRoot &&
            (emptyStateContent ? (
              <div className="py-2">{emptyStateContent}</div>
            ) : (
              <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/70 px-4 py-6 text-center">
                <p className="text-sm text-gray-500">{emptyMessage}</p>
              </div>
            ))}
          {!isTreeEmpty &&
            effectiveExpandedFolders &&
            displayTree.map((node, index) => (
              <FileTreeItem
                key={node.path}
                node={node}
                modifiedFilePaths={modifiedFilePaths}
                depth={0}
                isLast={index === displayTree.length - 1}
                ancestorHasNextSiblings={[]}
                selectedPath={selectedPath}
                activeDirectoryPath={activeDirectoryPath}
                onSelect={path => {
                  onSelectDirectory?.(null);
                  onSelectFile(path);
                }}
                onSelectDirectory={onSelectDirectory}
                expandedFolders={effectiveExpandedFolders}
                onToggleFolder={toggleFolder}
                onMoveNode={onMoveNode}
                draggedItem={draggedItem}
                onDragStateChange={setDraggedItem}
                onDeleteNode={onDeleteNode}
                onCreateFile={onCreateFile ?? undefined}
                onCreateFolder={onCreateFolder ?? undefined}
                onRequestRename={onRequestRename}
                pendingAction={pendingAction}
                onPendingActionValueChange={onPendingActionValueChange}
                onSubmitPendingAction={onSubmitPendingAction}
                onCancelPendingAction={onCancelPendingAction}
                readOnly={readOnly}
                isNodeProtected={isNodeProtected}
                renderFileIcon={renderFileIcon}
              />
            ))}
        </div>
      </div>
    </div>
  );
};
